const GOOGLE_MAPS_SCRIPT_ID = 'thinklet-google-maps-script';
const GOOGLE_MAPS_READY_MAX_ATTEMPTS = 50;
const GOOGLE_MAPS_READY_INTERVAL_MS = 100;

let googleMapsLoadPromise: Promise<void> | null = null;

const isGoogleMapsReady = () => Boolean(window.google?.maps?.MapTypeId);

const waitForGoogleMapsReady = () =>
  new Promise<void>((resolve, reject) => {
    if (isGoogleMapsReady()) {
      resolve();
      return;
    }

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (isGoogleMapsReady()) {
        window.clearInterval(timer);
        resolve();
        return;
      }
      if (attempts >= GOOGLE_MAPS_READY_MAX_ATTEMPTS) {
        window.clearInterval(timer);
        reject(new Error('Google Maps APIの初期化がタイムアウトしました'));
      }
    }, GOOGLE_MAPS_READY_INTERVAL_MS);
  });

export const ensureGoogleMapsApi = () => {
  if (isGoogleMapsReady()) {
    return Promise.resolve();
  }
  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY_HERE') {
    return Promise.reject(new Error('Google Maps APIキーが設定されていません'));
  }

  const existingScript =
    (document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null) ||
    (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]') as HTMLScriptElement | null);

  googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
    const resolveWhenReady = () => {
      void waitForGoogleMapsReady().then(resolve).catch(reject);
    };

    if (existingScript) {
      if (!existingScript.id) {
        existingScript.id = GOOGLE_MAPS_SCRIPT_ID;
      }
      existingScript.addEventListener('load', resolveWhenReady, { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Google Maps APIの読み込みに失敗しました')), {
        once: true,
      });
      resolveWhenReady();
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry&loading=async`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', resolveWhenReady, { once: true });
    script.addEventListener('error', () => reject(new Error('Google Maps APIの読み込みに失敗しました')), {
      once: true,
    });
    document.head.appendChild(script);
  }).catch((error) => {
    googleMapsLoadPromise = null;
    throw error;
  });

  return googleMapsLoadPromise;
};
